// src/components/CreatorApplication.jsx
//
// Comprehensive Creator screening application.
//
// Why a thorough form: YouTube TOS violations on a Common Channel suspend the
// entire channel (not just the offending creator), wiping revenue for everyone
// sharing that channel. The application is the gate that keeps risky creators
// out. We collect:
//   - Identity (name, email, phone) + language(s) for routing
//   - Reach (primary platform, follower range, sample work, social proof)
//   - Content (24-category multi-select, why join, attribution source, prior
//     strikes self-declaration)
//   - Legal commitments
//
// Disposable / typo emails are blocked server-side. Frontend also runs a
// client-side check for faster UX feedback.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Steps,
  Form,
  Input,
  Select,
  InputNumber,
  Checkbox,
  Button,
  Alert,
  Typography,
  Space,
  Progress,
  Result,
  Tag,
  Radio,
} from 'antd';
import { API_BASE_URL } from '../config';
import TurnstileWidget from './common/TurnstileWidget';
import HoneypotField from './common/HoneypotField';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const STEP_TITLES = [
  'About You',
  'Channel & Reach',
  'Content & Eligibility',
  'Legal',
  'Review',
];

// ----------------------------------------------------------------------------
// Reference data
// ----------------------------------------------------------------------------

// 24-category multi-select. Creators commonly produce across categories
// (e.g. comedy + music videos) so single-select is too narrow.
const CONTENT_CATEGORIES = [
  'Short Films',
  'Music Videos',
  'Stand-up Comedy',
  'Sketch Comedy',
  'Web Series',
  'Documentaries',
  'Vlogs',
  'Travel',
  'Food & Cooking',
  'Lifestyle',
  'Beauty & Fashion',
  'Education / Tutorials',
  'Gaming',
  'Tech Reviews',
  'Business / Finance',
  'Health & Fitness',
  'Sports',
  'News & Commentary',
  'Kids / Family',
  'Pets / Animals',
  'Art / Design',
  'Dance',
  'Religion / Spirituality',
  'Other',
];

// Platforms shown as checkboxes. When checked, a URL input appears for that
// platform. If the user checks nothing, they're a brand-new creator and we
// route their screening through the "Why join" step instead.
const PLATFORM_LIST = [
  { key: 'YouTube',   placeholder: 'https://youtube.com/c/yourchannel' },
  { key: 'Instagram', placeholder: 'https://instagram.com/yourhandle' },
  { key: 'TikTok',    placeholder: 'https://tiktok.com/@yourhandle' },
  { key: 'Facebook',  placeholder: 'https://facebook.com/yourpage' },
  { key: 'X (Twitter)', placeholder: 'https://x.com/yourhandle' },
  { key: 'Other',     placeholder: 'https://...' },
];

const FOLLOWER_RANGES = [
  '< 1k',
  '1k – 10k',
  '10k – 100k',
  '100k – 1M',
  '1M+',
];

const LANGUAGES = [
  'English', 'Hindi', 'Tamil', 'Telugu', 'Malayalam', 'Kannada',
  'Marathi', 'Bengali', 'Punjabi', 'Gujarati', 'Urdu',
  'Arabic', 'Spanish', 'Portuguese', 'French', 'German',
  'Indonesian', 'Filipino', 'Vietnamese', 'Thai', 'Mandarin', 'Other',
];

const FOUND_VIA = [
  'Google / web search',
  'YouTube',
  'Instagram',
  'TikTok',
  'Twitter / X',
  'Friend / referral',
  'Existing creator I know',
  'Press / article',
  'Other',
];

// Disposable email domains — same source as backend. Frontend-side check is
// purely for UX speed; the server is authoritative.
const DISPOSABLE_HINT_DOMAINS = new Set([
  'mailinator.com', 'tempmail.com', 'temp-mail.org', '10minutemail.com',
  'guerrillamail.com', 'yopmail.com', 'trashmail.com', 'maildrop.cc',
  'getairmail.com', 'sharklasers.com', 'throwawaymail.com', 'fakeinbox.com',
  'dispostable.com', 'mintemail.com', 'spamgourmet.com',
]);

function isDisposableHint(email) {
  if (!email || typeof email !== 'string') return false;
  const domain = email.toLowerCase().split('@')[1];
  return DISPOSABLE_HINT_DOMAINS.has(domain);
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const CreatorApplication = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [honeypot, setHoneypot] = useState('');

  const [formData, setFormData] = useState({
    // Step 0 — About You
    fullName: '',
    email: '',
    phone: '',
    languages: [],

    // Step 1 — Channel & Reach
    // Which platforms the creator is active on. Empty array = brand-new
    // creator with no channel anywhere yet.
    platforms: [],
    // Per-platform URL map. Keys mirror PLATFORM_LIST entries.
    platformUrls: {},
    followerRange: undefined,
    sampleUrl1: '',
    sampleUrl2: '',
    sampleUrl3: '',

    // Step 2 — Content & Eligibility
    contentCategories: [], // multi-select, 1+ required
    monthlyVideos: undefined,
    whyJoin: '',
    foundVia: undefined,
    priorStrikes: undefined, // 'NO' | 'YES_DETAILED' | 'YES_RESOLVED'
    priorStrikesNote: '',

    // Step 3 — Legal
    termsAccepted: false,
    contentRightsTransfer: false,
    indemnificationAgreement: false,
    revenueShareAgreement: false,
    strikePolicy: false,
  });

  useEffect(() => {
    const savedData = localStorage.getItem('applicationFormData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData((prev) => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Error loading saved form data', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('applicationFormData', JSON.stringify(formData));
  }, [formData]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Convenience helpers for the platform checkbox group.
  const togglePlatform = (key, checked) => {
    setFormData((prev) => {
      const has = (prev.platforms || []).includes(key);
      const next = checked
        ? (has ? prev.platforms : [...(prev.platforms || []), key])
        : (prev.platforms || []).filter((k) => k !== key);
      // If unticking, also clear the URL for that platform
      const urls = { ...(prev.platformUrls || {}) };
      if (!checked) delete urls[key];
      return { ...prev, platforms: next, platformUrls: urls };
    });
  };

  const setPlatformUrl = (key, url) => {
    setFormData((prev) => ({
      ...prev,
      platformUrls: { ...(prev.platformUrls || {}), [key]: url },
    }));
  };

  const validateStep = () => {
    setError('');
    switch (step) {
      case 0: {
        if (!formData.fullName || !formData.email || !formData.phone) {
          setError('Please fill in name, email, and phone.');
          return false;
        }
        if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
          setError('Please enter a valid email address.');
          return false;
        }
        if (isDisposableHint(formData.email)) {
          setError(
            'Please use a permanent email address. Disposable / temporary emails are not accepted.'
          );
          return false;
        }
        if (!/^[0-9+\s()-]{8,18}$/.test(formData.phone)) {
          setError('Please enter a valid phone number.');
          return false;
        }
        if (!formData.languages || formData.languages.length === 0) {
          setError('Please select at least one content language.');
          return false;
        }
        return true;
      }
      case 1: {
        // All URL fields are optional. Anything that is filled in must be
        // a real https URL though.
        const urls = formData.platformUrls || {};
        for (const key of Object.keys(urls)) {
          const u = urls[key];
          if (u && !/^https?:\/\//.test(u)) {
            setError(`${key} URL should start with https://`);
            return false;
          }
        }
        if (!formData.followerRange) {
          setError('Please select your follower / subscriber range.');
          return false;
        }
        return true;
      }
      case 2: {
        if (!formData.contentCategories || formData.contentCategories.length === 0) {
          setError('Please pick at least one content category.');
          return false;
        }
        if (formData.contentCategories.length > 5) {
          setError('Pick up to 5 categories that fit you best.');
          return false;
        }
        if (
          !formData.monthlyVideos ||
          isNaN(parseInt(formData.monthlyVideos, 10)) ||
          parseInt(formData.monthlyVideos, 10) <= 0
        ) {
          setError('Please tell us how many videos you can publish per month.');
          return false;
        }
        if (!formData.whyJoin || formData.whyJoin.trim().length < 80) {
          setError('Please tell us a bit more (at least 80 characters).');
          return false;
        }
        if (!formData.foundVia) {
          setError('Please tell us how you heard about Choice MCN.');
          return false;
        }
        if (!formData.priorStrikes) {
          setError('Please answer the YouTube strikes question.');
          return false;
        }
        if (
          (formData.priorStrikes === 'YES_DETAILED' ||
            formData.priorStrikes === 'YES_RESOLVED') &&
          (!formData.priorStrikesNote || formData.priorStrikesNote.trim().length < 20)
        ) {
          setError('Please briefly explain the strike / termination history.');
          return false;
        }
        return true;
      }
      case 3: {
        if (
          !formData.termsAccepted ||
          !formData.contentRightsTransfer ||
          !formData.indemnificationAgreement ||
          !formData.revenueShareAgreement ||
          !formData.strikePolicy
        ) {
          setError('You must accept all agreements to proceed.');
          return false;
        }
        return true;
      }
      default:
        return true;
    }
  };

  const next = () => {
    if (validateStep()) {
      setStep((s) => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const prev = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    if (!turnstileToken) {
      setError('Please complete the bot check below before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // Derive backward-compat fields from the new multi-platform structure.
      // The first checked platform with a URL becomes the canonical
      // "youtubeChannel" / "primaryChannelUrl" the backend expects.
      const platformUrls = formData.platformUrls || {};
      const firstPlatformWithUrl =
        (formData.platforms || []).find((p) => platformUrls[p]) || '';
      const legacyPrimaryUrl = firstPlatformWithUrl
        ? platformUrls[firstPlatformWithUrl]
        : '';

      const payload = {
        // legacy / canonical
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        niche: formData.contentCategories[0] || 'Other',
        youtubeChannel: legacyPrimaryUrl,
        monthlyVideos: formData.monthlyVideos,
        aboutContent: formData.whyJoin,
        // legal
        termsAccepted: formData.termsAccepted,
        contentRightsTransfer: formData.contentRightsTransfer,
        indemnificationAgreement: formData.indemnificationAgreement,
        revenueShareAgreement: formData.revenueShareAgreement,
        strikePolicy: formData.strikePolicy,
        // NEW structured screening fields
        applicationType: 'CREATOR',
        languages: formData.languages,
        primaryPlatform: firstPlatformWithUrl || 'NEW',
        primaryChannelUrl: legacyPrimaryUrl,
        platforms: formData.platforms || [],
        platformUrls: platformUrls,
        followerRange: formData.followerRange,
        sampleUrls: [formData.sampleUrl1, formData.sampleUrl2, formData.sampleUrl3]
          .filter(Boolean),
        contentCategories: formData.contentCategories,
        whyJoin: formData.whyJoin,
        foundVia: formData.foundVia,
        priorStrikes: formData.priorStrikes,
        priorStrikesNote: formData.priorStrikesNote,
        // security
        turnstileToken,
        website: honeypot,
      };

      const response = await fetch(`${API_BASE_URL}/api/applications/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to submit application');
      }
      setSuccess(true);
      setApplicationId(data.id);
      localStorage.setItem('applicationId', data.id);
      localStorage.setItem('applicationEmail', formData.email);
      localStorage.removeItem('applicationFormData');

      if (window.gtag) {
        window.gtag('event', 'application_submitted', {
          event_category: 'conversion',
          event_label: formData.contentCategories[0] || 'Creator',
        });
      }

      setTimeout(() => {
        navigate('/application/confirmation', {
          state: { applicationId: data.id, email: formData.email },
        });
      }, 2000);
    } catch (err) {
      setError(err.message);
      console.error('Application submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 32 }}>
        <Result
          status="success"
          title="Application Submitted Successfully"
          subTitle={
            <span>
              Your application ID is <strong>{applicationId}</strong>. We'll
              review and email you within 5–7 business days.
            </span>
          }
        />
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // Step bodies
  // --------------------------------------------------------------------------

  const renderStepBody = () => {
    switch (step) {
      case 0:
        return (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={4}>About You</Title>
            <Paragraph type="secondary">
              Let's start with your basics. Use a permanent email — disposable
              addresses are blocked.
            </Paragraph>
            <Form.Item label="Full Name" required>
              <Input
                value={formData.fullName}
                onChange={(e) => setField('fullName', e.target.value)}
                placeholder="Your full name"
              />
            </Form.Item>
            <Form.Item label="Email Address" required>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="you@example.com"
              />
            </Form.Item>
            <Form.Item label="Phone Number" required>
              <Input
                value={formData.phone}
                onChange={(e) => setField('phone', e.target.value)}
                placeholder="+91 98765 43210"
              />
            </Form.Item>
            <Form.Item
              label="Primary content language(s)"
              required
              tooltip="Pick every language you publish in. Helps us match you to the right Common Channels."
            >
              <Select
                mode="multiple"
                value={formData.languages}
                onChange={(v) => setField('languages', v)}
                placeholder="Pick one or more"
                showSearch
                optionFilterProp="children"
              >
                {LANGUAGES.map((l) => (
                  <Option key={l} value={l}>{l}</Option>
                ))}
              </Select>
            </Form.Item>
          </Space>
        );

      case 1:
        return (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={4}>Your Channel & Reach</Title>
            <Paragraph type="secondary">
              Where you create now and how big your audience is. We accept
              creators from any platform — you don't have to be YouTube-first.
            </Paragraph>

            {/* Multi-checkbox: pick any/all platforms you create on. Each
                checked platform reveals a URL input. Leaving them all
                unchecked = brand-new creator with no channel anywhere. */}
            <Form.Item label="Which platforms do you create on?">
              <Space direction="vertical" size={6}>
                {PLATFORM_LIST.map((p) => (
                  <Checkbox
                    key={p.key}
                    checked={(formData.platforms || []).includes(p.key)}
                    onChange={(e) => togglePlatform(p.key, e.target.checked)}
                  >
                    {p.key}
                  </Checkbox>
                ))}
                {(formData.platforms || []).length === 0 && (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Leave all unchecked if you don't have a channel anywhere yet.
                  </Text>
                )}
              </Space>
            </Form.Item>

            {/* URL inputs appear for each checked platform. All optional. */}
            {PLATFORM_LIST.filter((p) =>
              (formData.platforms || []).includes(p.key)
            ).map((p) => (
              <Form.Item key={p.key} label={`${p.key} URL`}>
                <Input
                  value={(formData.platformUrls || {})[p.key] || ''}
                  onChange={(e) => setPlatformUrl(p.key, e.target.value)}
                  placeholder={p.placeholder}
                />
              </Form.Item>
            ))}

            <Form.Item label="Subscriber / follower count" required>
              <Select
                value={formData.followerRange}
                onChange={(v) => setField('followerRange', v)}
                placeholder="Roughly how big is your audience? Pick '< 1k' if none."
              >
                {FOLLOWER_RANGES.map((r) => (
                  <Option key={r} value={r}>{r}</Option>
                ))}
              </Select>
            </Form.Item>

            {/* Sample work is OPTIONAL too. Encouraged but not required so
                brand-new creators can apply. */}
            <Form.Item
              label="Sample work - 1 to 3 video links (optional)"
              tooltip="If you have published work, share your best. Helps the review team see your style."
            >
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Input
                  value={formData.sampleUrl1}
                  onChange={(e) => setField('sampleUrl1', e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                />
                <Input
                  value={formData.sampleUrl2}
                  onChange={(e) => setField('sampleUrl2', e.target.value)}
                  placeholder="https://instagram.com/reel/..."
                />
                <Input
                  value={formData.sampleUrl3}
                  onChange={(e) => setField('sampleUrl3', e.target.value)}
                  placeholder="https://tiktok.com/@.../video/..."
                />
              </Space>
            </Form.Item>
          </Space>
        );

      case 2:
        return (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={4}>Content & Eligibility</Title>
            <Paragraph type="secondary">
              The screening questions. We're strict about TOS history because
              one strike on a Common Channel hurts every creator on it.
            </Paragraph>

            <Form.Item
              label="Content categories you create"
              required
              tooltip="Pick up to 5 that fit you. We match creators to Common Channels by category."
            >
              <Select
                mode="multiple"
                value={formData.contentCategories}
                onChange={(v) => setField('contentCategories', v)}
                placeholder="Pick all that apply (max 5)"
                showSearch
                optionFilterProp="children"
                maxTagCount={6}
              >
                {CONTENT_CATEGORIES.map((c) => (
                  <Option key={c} value={c}>{c}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Videos you can publish per month" required>
              <InputNumber
                min={1}
                max={50}
                value={formData.monthlyVideos}
                onChange={(v) => setField('monthlyVideos', v)}
                style={{ width: '100%' }}
                placeholder="e.g. 4"
              />
            </Form.Item>

            <Form.Item
              label="Why do you want to join Choice MCN?"
              required
              tooltip="Tell us about your goals, your audience, and what you'd add to a Common Channel."
            >
              <TextArea
                rows={5}
                value={formData.whyJoin}
                onChange={(e) => setField('whyJoin', e.target.value)}
                placeholder="Minimum 80 characters. Any language is OK."
                maxLength={800}
                showCount
              />
            </Form.Item>

            <Form.Item label="How did you hear about Choice MCN?" required>
              <Select
                value={formData.foundVia}
                onChange={(v) => setField('foundVia', v)}
                placeholder="Pick one"
              >
                {FOUND_VIA.map((s) => (
                  <Option key={s} value={s}>{s}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Have any of your channels received YouTube strikes or been terminated?"
              required
              tooltip="Be honest. Our review team checks public records. Hidden strike history is grounds for rejection."
            >
              <Radio.Group
                value={formData.priorStrikes}
                onChange={(e) => setField('priorStrikes', e.target.value)}
              >
                <Space direction="vertical">
                  <Radio value="NO">No, never</Radio>
                  <Radio value="YES_RESOLVED">
                    Yes, but the strike has been resolved / expired
                  </Radio>
                  <Radio value="YES_DETAILED">
                    Yes, channel was terminated or strike is still active
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {(formData.priorStrikes === 'YES_RESOLVED' ||
              formData.priorStrikes === 'YES_DETAILED') && (
              <Form.Item label="Briefly explain the situation" required>
                <TextArea
                  rows={3}
                  value={formData.priorStrikesNote}
                  onChange={(e) => setField('priorStrikesNote', e.target.value)}
                  placeholder="What happened, when, and the current status."
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            )}
          </Space>
        );

      case 3:
        return (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={4}>Legal Agreements</Title>
            <Paragraph type="secondary">
              Please review and accept the terms before continuing.
            </Paragraph>
            <Checkbox
              checked={formData.termsAccepted}
              onChange={(e) => setField('termsAccepted', e.target.checked)}
            >
              I accept the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </Checkbox>
            <Checkbox
              checked={formData.contentRightsTransfer}
              onChange={(e) => setField('contentRightsTransfer', e.target.checked)}
            >
              I agree to transfer exclusive content rights for content uploaded
              through the Choice MCN platform.
            </Checkbox>
            <Checkbox
              checked={formData.indemnificationAgreement}
              onChange={(e) =>
                setField('indemnificationAgreement', e.target.checked)
              }
            >
              I agree to indemnify Choice MCN against any policy violations
              related to my content.
            </Checkbox>
            <Checkbox
              checked={formData.revenueShareAgreement}
              onChange={(e) =>
                setField('revenueShareAgreement', e.target.checked)
              }
            >
              I accept the revenue share agreement as displayed in my dashboard
              once approved (typically 70–80% creator share).
            </Checkbox>
            <Checkbox
              checked={formData.strikePolicy}
              onChange={(e) => setField('strikePolicy', e.target.checked)}
            >
              I understand my contract may be terminated immediately if my
              content causes a YouTube policy strike on the Common Channel.
            </Checkbox>
          </Space>
        );

      case 4:
        return (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={4}>Review & Submit</Title>
            <Paragraph type="secondary">
              Quick recap. Edit any step using the Previous button.
            </Paragraph>

            <Card type="inner" title="About You">
              <p><Text strong>Name:</Text> {formData.fullName}</p>
              <p><Text strong>Email:</Text> {formData.email}</p>
              <p><Text strong>Phone:</Text> {formData.phone}</p>
              <p>
                <Text strong>Languages:</Text>{' '}
                {(formData.languages || []).map((l) => (
                  <Tag key={l}>{l}</Tag>
                ))}
              </p>
            </Card>

            <Card type="inner" title="Channel & Reach">
              <p>
                <Text strong>Platforms:</Text>{' '}
                {(formData.platforms || []).length === 0 ? (
                  <Text type="secondary">Brand-new creator (no channels yet)</Text>
                ) : (
                  (formData.platforms || []).map((p) => <Tag key={p}>{p}</Tag>)
                )}
              </p>
              {(formData.platforms || [])
                .filter((p) => (formData.platformUrls || {})[p])
                .map((p) => (
                  <p key={p}>
                    <Text strong>{p}:</Text>{' '}
                    {(formData.platformUrls || {})[p]}
                  </p>
                ))}
              <p><Text strong>Follower range:</Text> {formData.followerRange}</p>
              {[formData.sampleUrl1, formData.sampleUrl2, formData.sampleUrl3]
                .filter(Boolean).length > 0 && (
                <>
                  <p><Text strong>Sample work:</Text></p>
                  <ul style={{ marginTop: 0 }}>
                    {[formData.sampleUrl1, formData.sampleUrl2, formData.sampleUrl3]
                      .filter(Boolean)
                      .map((u, i) => <li key={i}>{u}</li>)}
                  </ul>
                </>
              )}
            </Card>

            <Card type="inner" title="Content & Eligibility">
              <p>
                <Text strong>Categories:</Text>{' '}
                {(formData.contentCategories || []).map((c) => (
                  <Tag color="purple" key={c}>{c}</Tag>
                ))}
              </p>
              <p><Text strong>Videos / month:</Text> {formData.monthlyVideos}</p>
              <p><Text strong>Why join:</Text> {formData.whyJoin}</p>
              <p><Text strong>Heard about us via:</Text> {formData.foundVia}</p>
              <p>
                <Text strong>Prior strikes / termination:</Text>{' '}
                {formData.priorStrikes === 'NO'
                  ? 'None'
                  : formData.priorStrikes === 'YES_RESOLVED'
                    ? 'Yes — resolved'
                    : 'Yes — active / terminated'}
              </p>
              {formData.priorStrikesNote && (
                <p><Text strong>Notes:</Text> {formData.priorStrikesNote}</p>
              )}
            </Card>

            <Card type="inner" title="Legal">
              <p>You've accepted all required agreements.</p>
            </Card>

            <TurnstileWidget onVerify={setTurnstileToken} action="creator-apply" />
            {!turnstileToken && (
              <Alert
                type="warning"
                showIcon
                message="Complete the bot check above before submitting."
              />
            )}
          </Space>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <Card>
        <Title level={3} style={{ marginBottom: 8 }}>
          Creator Application
        </Title>
        <Paragraph type="secondary">
          Step {step + 1} of {STEP_TITLES.length} —{' '}
          <Text strong>{STEP_TITLES[step]}</Text>
        </Paragraph>

        <div className="hide-on-mobile" style={{ marginBottom: 24 }}>
          <Steps current={step} size="small" responsive={false}>
            {STEP_TITLES.map((t) => (
              <Steps.Step key={t} title={t} />
            ))}
          </Steps>
        </div>
        <div className="show-on-mobile" style={{ marginBottom: 24 }}>
          <Progress
            percent={Math.round(((step + 1) / STEP_TITLES.length) * 100)}
            size="small"
          />
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form layout="vertical" onFinish={handleSubmit}>
          {renderStepBody()}
          <HoneypotField value={honeypot} onChange={setHoneypot} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 24,
              gap: 12,
            }}
          >
            <Button onClick={prev} disabled={step === 0}>
              Previous
            </Button>
            {step < STEP_TITLES.length - 1 ? (
              <Button type="primary" onClick={next}>
                Next
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!turnstileToken}
              >
                Submit Application
              </Button>
            )}
          </div>
        </Form>
      </Card>

      <style>{`
        .show-on-mobile { display: none; }
        @media (max-width: 640px) {
          .hide-on-mobile { display: none; }
          .show-on-mobile { display: block; }
        }
      `}</style>
    </div>
  );
};

export default CreatorApplication;
